import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

export type ReportFilterKey =
  | 'eorAgency'
  | 'companies'
  | 'sites'
  | 'batches'
  | 'department'
  | 'branch'
  | 'employee'
  | 'ssn'
  | 'orderType'
  | 'orderStatus'
  | 'mspStatus'
  | 'companyStatus'
  | 'activeOnAssignment'
  | 'deductions'
  // Deduction Submittal only (STFFLG-3769) — narrow to one deduction name.
  | 'deductionName'
  // Comprehensive Order (STFFLG-3968) — lifecycle-based filters replacing orderStatus.
  | 'assignedAgency'
  | 'lifecycleStatus'
  | 'closedOutcome'
  // Accrued Hours (STFFLG-4345) — free-text order ID and minimum-hours threshold.
  | 'orderNumber'
  | 'minTotalHours'

/**
 * Report metadata returned from the backend
 */
export interface ReportMetadata {
  reportType: string
  displayName: string
  description: string
  category: string
  available: boolean
  applicableFilters: ReportFilterKey[]
  /** Controls the download path. 'pdf' uses a dedicated blob endpoint;
   *  'csv' and 'xlsx' use the standard download/:reportType route.
   *  'importer' is FE-only — clicks open a dedicated import modal instead. */
  output?: 'pdf' | 'csv' | 'xlsx' | 'importer'
  /** Platform-admin-only report (e.g. exemption-report). The backend already
   *  omits these from the catalog for non-platform-admin callers; the FE
   *  additionally hides them as a defensive gate. */
  platformAdminOnly?: boolean
  /** Date-type picker for reports that support multiple date semantics.
   *  When present the FE renders a Date Type selector and sends `dateType`
   *  and calendar dates on the wire. Reports without this field send ISO
   *  timestamps as before (byte-identical behaviour). */
  dateTypes?: {key: string; label: string}[]
  /** Which dateTypes[].key to pre-select. Must be one of the keys in dateTypes.
   *  Falls back to dateTypes[0].key when omitted. */
  defaultDateType?: string
}

/**
 * Parameters for downloading a report
 */
export interface ReportDownloadParams {
  reportType: string
  startDate: string
  endDate: string
  /** Controls which download path is used. 'pdf' routes to the dedicated PDF
   *  endpoint; omit or use 'csv'/'xlsx' for the standard tabular route. */
  output?: 'pdf' | 'csv' | 'xlsx'
  format?: 'csv' | 'xlsx'
  agencyId?: string
  eor?: boolean
  eorAgencyId?: string
  eorAgencyIds?: string[]
  companyId?: string
  companyIds?: string[]
  siteIds?: string[]
  batchIds?: string[]
  departmentId?: string
  branchId?: string
  branchIds?: string[]
  employeeId?: string
  ssn?: string
  orderTypes?: string[]
  orderStatuses?: string[]
  activeOnAssignment?: string
  paycodeIds?: string[]
  outputMode?: 'single' | 'multiple'
  /** Selected date-type key. Sent only for reports that declare dateTypes in their
   *  catalog entry; ignored otherwise. */
  dateType?: string
  /** MSP service-order status filter. Sent as string array; omit when empty. */
  mspStatuses?: string[]
  /** Company service-order status filter. Sent as string array; omit when empty. */
  companyStatuses?: string[]
  /** IANA time-zone name (e.g. "America/Los_Angeles"). Sent alongside calendar-date
   *  startDate/endDate so the BE can build correct UTC windows per the user's local day.
   *  Only sent for reports that declare dateTypes. */
  timeZone?: string
  /** Comprehensive Order: assigned-agency filter (STFFLG-3968). */
  assignedAgencyIds?: string[]
  /** Comprehensive Order: lifecycle status filter (STFFLG-3968). */
  lifecycleStatuses?: string[]
  /** Comprehensive Order: closed-order outcome filter (STFFLG-3968). */
  closedOutcomes?: string[]
  /** Accrued Hours: free-text order ID filter (STFFLG-4345). Omit when empty. */
  orderNumber?: string
  /** Accrued Hours: minimum total-hours threshold (STFFLG-4345). Omit when absent or invalid. */
  minTotalHours?: number
}

/**
 * Filter shape accepted by the custom-download and custom-preview endpoints.
 * Mirrors the backend DownloadReportRequest DTO.
 */
export interface CustomReportFilters {
  startDate: string
  endDate: string
  agencyId?: string
  eor?: boolean
  eorAgencyId?: string
  eorAgencyIds?: string[]
  companyId?: string
  companyIds?: string[]
  departmentId?: string
  branchId?: string
  branchIds?: string[]
  employeeId?: string
  ssn?: string
  orderTypes?: string[]
  orderStatuses?: string[]
  dateType?: string
  mspStatuses?: string[]
  companyStatuses?: string[]
  /** Comprehensive Order: assigned-agency filter (STFFLG-3968). */
  assignedAgencyIds?: string[]
  /** Comprehensive Order: lifecycle status filter (STFFLG-3968). */
  lifecycleStatuses?: string[]
  /** Comprehensive Order: closed-order outcome filter (STFFLG-3968). */
  closedOutcomes?: string[]
  /** Accrued Hours: free-text order ID filter (STFFLG-4345). Omit when empty. */
  orderNumber?: string
  /** Accrued Hours: minimum total-hours threshold (STFFLG-4345). Omit when absent or invalid. */
  minTotalHours?: number
}

export interface CustomReportFieldMeta {
  field: string
  header: string
  group: string
}

export interface CustomReportFieldCatalogEntry extends ReportMetadata {
  fields: CustomReportFieldMeta[]
}

export interface CustomReportColumn {
  field: string
  header?: string
}

export interface CustomReportRequest {
  dataSource: string
  columns: CustomReportColumn[]
  filters: CustomReportFilters
}

export interface CustomReportPreviewResponse {
  columns: Array<{field: string; header: string}>
  rows: Array<Record<string, unknown>>
  totalRows: number
  truncated: boolean
}

/**
 * Reports API Service
 * Handles all report-related API calls for the generic reports system
 * Extends BaseApi for automatic Bearer token authorization
 */
export class ReportsApi extends BaseApi {
  /**
   * Get list of all available report types
   * GET /payroll/reports/available
   *
   * @returns Array of available reports with their metadata
   */
  async getAvailableReports(search?: string): Promise<ReportMetadata[]> {
    try {
      const params = search ? {search} : undefined
      const response = await this.client.get<ReportMetadata[]>('/payroll/reports/available', {
        params,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching available reports:', error)
      throw error
    }
  }

  /**
   * STFFLG-3769: the distinct Deduction Name values available for the given filters.
   * POST /payroll/reports/deduction-submittal/deduction-names
   *
   * Populates the Deduction Submittal panel's "Deduction Name" dropdown. The backend
   * derives these from the same aggregation the report itself runs, so every option
   * is guaranteed to match at least one row — including garnishment and tax rows,
   * whose names are derived rather than coming from a paycode.
   *
   * POST for the same reason as downloadReport: the filter set includes `ssn`, which
   * must never appear in a URL or access log.
   */
  async getDeductionNames(filters: Record<string, unknown>): Promise<string[]> {
    try {
      const response = await this.client.post<string[]>(
        '/payroll/reports/deduction-submittal/deduction-names',
        filters,
      )
      return response.data ?? []
    } catch (error: unknown) {
      // Non-critical: an empty list just hides the filter rather than blocking
      // the user from generating the report.
      reportCatchError('Error fetching deduction names:', error)
      return []
    }
  }

  /**
   * Download a report as CSV or Excel.
   * POST /payroll/reports/download/:reportType
   *
   * Filters (including PII-sensitive fields such as `ssn`) are sent in the request
   * body — never as query parameters — so they are not captured by access logs,
   * browser history, or upstream proxies.
   *
   * The `format` field (default: 'csv') is forwarded to the backend which sets
   * the appropriate Content-Type and file extension.
   *
   * @param params - Report download parameters
   * @returns File as Blob plus the Content-Disposition header
   */
  async downloadReport(
    params: ReportDownloadParams,
  ): Promise<{blob: Blob; contentDisposition: string}> {
    try {
      const {reportType, ...filters} = params

      const response = await this.client.post(`/payroll/reports/download/${reportType}`, filters, {
        responseType: 'blob',
      })
      return {
        blob: response.data as Blob,
        contentDisposition: (response.headers['content-disposition'] as string) ?? '',
      }
    } catch (error: unknown) {
      reportCatchError('Error downloading report:', error)
      const message = await this.extractBlobErrorMessage(error)
      throw message ? new Error(message) : error
    }
  }

  /**
   * Fetch the field catalog for the custom report builder.
   * GET /payroll/reports/fields
   */
  async getFieldCatalog(): Promise<CustomReportFieldCatalogEntry[]> {
    try {
      const response = await this.client.get<CustomReportFieldCatalogEntry[]>(
        '/payroll/reports/fields',
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching report field catalog:', error)
      throw error
    }
  }

  /**
   * Preview a custom report. Returns the first 25 rows projected onto the
   * requested columns.
   * POST /payroll/reports/custom-preview
   */
  async previewCustomReport(request: CustomReportRequest): Promise<CustomReportPreviewResponse> {
    try {
      const response = await this.client.post<CustomReportPreviewResponse>(
        '/payroll/reports/custom-preview',
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error previewing custom report:', error)
      throw error
    }
  }

  /**
   * Download a custom report. Format is passed as a path segment so the
   * server can set the appropriate content-type and extension.
   * POST /payroll/reports/custom-download/:format
   */
  async downloadCustomReport(
    request: CustomReportRequest,
    format: 'csv' | 'xlsx' = 'csv',
  ): Promise<Blob> {
    try {
      const response = await this.client.post(
        `/payroll/reports/custom-download/${format}`,
        request,
        {responseType: 'blob'},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error downloading custom report:', error)
      const message = await this.extractBlobErrorMessage(error)
      throw message ? new Error(message) : error
    }
  }

  /**
   * Download the Paycheck Sign-Off report as a PDF.
   * POST /payroll/reports/paycheck-signoff-pdf
   *
   * The response is an application/pdf binary. Filters map directly from the
   * report catalog's applicableFilters: eorAgency→agencyIds, companies→companyIds,
   * batches→batchIds.
   */
  async downloadPaycheckSignoffPdf(body: {
    startDate: string
    endDate: string
    agencyIds?: string[]
    companyIds?: string[]
    batchIds?: string[]
  }): Promise<{blob: Blob; contentDisposition: string}> {
    try {
      const response = await this.client.post('/payroll/reports/paycheck-signoff-pdf', body, {
        responseType: 'blob',
      })
      return {
        blob: response.data as Blob,
        contentDisposition: (response.headers['content-disposition'] as string) ?? '',
      }
    } catch (error: unknown) {
      reportCatchError('Error downloading paycheck sign-off PDF:', error)
      const message = await this.extractBlobErrorMessage(error)
      throw message ? new Error(message) : error
    }
  }

  /**
   * Download the Employee Paystubs report as a PDF (single) or ZIP (multiple).
   * POST /payroll/reports/employee-paystubs-pdf
   *
   * outputMode 'single' → one merged PDF; 'multiple' → ZIP of individual PDFs.
   * Filename always comes from the Content-Disposition header.
   * ssn must be exactly 4 or 9 digits when supplied.
   */
  async downloadEmployeePaystubsPdf(body: {
    startDate: string
    endDate: string
    eorAgencyIds?: string[]
    companyIds?: string[]
    siteIds?: string[]
    departmentId?: string
    employeeId?: string
    ssn?: string
    outputMode?: 'single' | 'multiple'
  }): Promise<{blob: Blob; contentDisposition: string}> {
    try {
      const response = await this.client.post('/payroll/reports/employee-paystubs-pdf', body, {
        responseType: 'blob',
      })
      return {
        blob: response.data as Blob,
        contentDisposition: (response.headers['content-disposition'] as string) ?? '',
      }
    } catch (error: unknown) {
      reportCatchError('Error downloading employee paystubs PDF:', error)
      const message = await this.extractBlobErrorMessage(error)
      throw message ? new Error(message) : error
    }
  }

  triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

// Export a singleton instance
export const reportsApi = new ReportsApi()

export default reportsApi
